import { useCallback } from 'react';
import type { Control, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { DatePicker } from './DatePicker';
import FieldErrors from './FieldErrors';

export type TDatePickerFormFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  fieldPath: TFieldPath;
  rules?: UseControllerProps<TFieldValues, TFieldPath>['rules'];
  placeholder?: string;
  onBlur?: () => void;
  onChange?: (value: Date | null) => void;
};

export const DatePickerFormField = <
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  fieldPath,
  control,
  rules,
  placeholder,
  onBlur,
  onChange,
}: TDatePickerFormFieldProps<TFieldValues, TFieldPath>) => {
  const {
    field: { onBlur: innerOnBlur, onChange: innerOnChange, ref, value },
    fieldState: { invalid, error },
  } = useController({ name: fieldPath, control, rules });

  const composedOnBlur = useCallback(() => {
    innerOnBlur();
    if (onBlur) {
      onBlur();
    }
  }, [onBlur, innerOnBlur]);

  const composedOnChange = useCallback(
    (selectedValue: Date | null) => {
      innerOnChange(selectedValue);
      onChange?.(selectedValue);
    },
    [onChange, innerOnChange],
  );

  return (
    <div>
      <DatePicker
        className='form-input'
        value={value}
        onChange={composedOnChange}
        ref={ref}
        onBlur={composedOnBlur}
        placeholder={placeholder}
      />
      {invalid && error && <FieldErrors error={error} />}
    </div>
  );
};
